
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { isSuperUser } from "../config/roles";
import { 
    listComplianceCards, 
    createComplianceCard, 
    deleteComplianceCard, 
    runAuthorityBackfill 
} from "../services/complianceService";
import { ComplianceCard } from "../components/compliance/ComplianceCard";
import { TrackedAuthoritiesDialog } from "../components/compliance/TrackedAuthoritiesDialog";
import { RiskScoreInfo } from "../components/compliance/RiskScoreInfo";
import { ComplianceCard as IComplianceCard } from "../types/compliance";
import { useToast } from "../components/Toast";
import { Modal } from "../components/Modal";
import { useAiContext } from "../ai/AiContext";
import { COL_COMPLIANCE_CARDS } from "../constants/collections";

const ProjectGridItem = ({ card, onClick, onDelete, canManage }: any) => {
    // Risk Display
    const riskScore = card.riskScore || 0;
    const riskLevel = card.riskLevel || (riskScore >= 80 ? "HIGH" : riskScore >= 50 ? "MEDIUM" : "LOW");
    let riskColor = riskLevel === 'HIGH' ? '#ef4444' : riskLevel === 'MEDIUM' ? '#f59e0b' : '#10b981';
    
    // Fallback fields for display
    const regulator = card.regulatorName || card.sectionA?.regulatorName || "待補齊";
    const owner = card.ownerDeptName || card.sectionB?.ownerDeptName || "未設定";
    const title = card.title || card.sectionA?.title || "(無標題)";

    return (
        <div 
            style={{
                background: '#1e1e1e', border: '1px solid #333', borderRadius: 12, padding: 16, 
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                minHeight: 180, cursor: 'pointer', position: 'relative'
            }}
            onClick={onClick}
        >
            <div>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom: 8}}>
                    <span className="badge" style={{color: riskColor, borderColor: riskColor, fontSize: 10, padding:'2px 6px'}}>
                        {riskLevel} ({riskScore})
                    </span>
                    <span className="badge" style={{background: '#333', color: '#ccc', border:'none', fontSize: 10}}>
                        {card.status}
                    </span>
                </div>
                
                <h3 style={{fontSize: 15, fontWeight: 'bold', color: '#fff', marginBottom: 8, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>
                    {title}
                </h3>
                
                <div style={{fontSize: 12, color: '#888', display:'flex', flexDirection:'column', gap: 4}}>
                    <div style={{color:'#ccc'}}>🏛️ {regulator}</div>
                    <div style={{color:'#a5b4fc'}}>🏢 {owner}</div>
                </div>
            </div>

            {canManage && (
                <button 
                    style={{position:'absolute', bottom:12, right:12, background:'transparent', border:'none', color:'var(--danger)', cursor:'pointer', fontSize:12}}
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                >
                    刪除
                </button>
            )}
        </div>
    );
};

export function ComplianceControlCenterPage() {
    const { user } = useAuth();
    const { showToast } = useToast();
    const { requestDelete } = useAiContext();
    
    // Tab State Management (URL Sync)
    const getInitialTab = () => {
        const params = new URLSearchParams(window.location.search);
        const t = params.get('tab');
        if (t === 'project') return 'PROJECT';
        if (t === 'history') return 'HISTORY';
        return 'INTEL';
    };

    const [activeTab, setActiveTab] = useState<'INTEL' | 'PROJECT' | 'HISTORY'>(getInitialTab);
    const [cards, setCards] = useState<IComplianceCard[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedCard, setSelectedCard] = useState<IComplianceCard | null>(null);
    const [showCrawlerConfig, setShowCrawlerConfig] = useState(false);

    const isSuper = isSuperUser(user?.email);

    // Sync URL when tab changes
    useEffect(() => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', activeTab.toLowerCase());
        window.history.replaceState({}, '', url.toString());
        fetchData(); 
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const type = activeTab === 'INTEL' ? 'intel' : activeTab === 'PROJECT' ? 'project' : undefined;
            const list = await listComplianceCards(type, activeTab === 'HISTORY');
            setCards(list);
        } catch(e: any) { 
            console.error(e);
            setErrorMsg(e.message || "資料讀取失敗");
            showToast("資料讀取失敗", "error");
        } finally { 
            setLoading(false); 
        }
    };

    const handleDelete = (card: IComplianceCard) => {
        requestDelete({
            targetType: 'complianceCard',
            targetId: card.id,
            summary: card.title,
            onConfirm: async () => {
                await deleteComplianceCard(card.id);
                showToast("已刪除");
                fetchData();
            }
        });
    };

    const handleCreate = async () => {
        await createComplianceCard({
            type: activeTab === 'INTEL' ? 'intel' : 'project',
            title: "新專案/情資",
            status: 'DRAFT',
            riskLevel: "LOW"
        });
        fetchData();
    };

    const handleFixData = async () => {
        if(!confirm("確定要執行「資料修復」？將補齊缺少的欄位 (Type, Regulator, DeptName)，不會覆蓋現有資料。")) return;
        setRefreshing(true);
        try {
            const res = await runAuthorityBackfill();
            showToast(`修復完成：${res.fixed} 筆資料已更新`);
            // Critical: Re-fetch data to reflect changes immediately
            await fetchData();
        } catch(e: any) {
            showToast(e.message, "error");
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <h2 style={{margin:0, fontSize: 20, color: '#fff'}}>
                    法遵情資與專案治理中心
                    {isSuper && (
                        <span style={{fontSize:10, marginLeft:10, color:'#666', fontWeight:'normal'}}>
                            [Debug: {activeTab} | {cards.length} items | {COL_COMPLIANCE_CARDS}]
                        </span>
                    )}
                </h2>
                <div style={{display:'flex', gap: 8}}>
                    {isSuper && <button className="smallbtn" onClick={() => setShowCrawlerConfig(true)}>⚙️ 設定</button>}
                </div>
            </div>
            
            <div className="row" style={{borderBottom: '1px solid #333', marginBottom: 16}}>
                <button className={`navbtn ${activeTab==='INTEL'?'active':''}`} onClick={()=>setActiveTab('INTEL')}>法遵情資</button>
                <button className={`navbtn ${activeTab==='PROJECT'?'active':''}`} onClick={()=>setActiveTab('PROJECT')}>專案管控</button>
                <button className={`navbtn ${activeTab==='HISTORY'?'active':''}`} onClick={()=>setActiveTab('HISTORY')}>歷史彙編</button>
            </div>
            
            {/* Risk Score Explanation - Visible for INTEL and PROJECT tabs */}
            {activeTab !== 'HISTORY' && <RiskScoreInfo />}

            <div style={{flex: 1, overflowY: 'auto', paddingBottom: 40}}>
                {/* Actions Bar */}
                {!loading && isSuper && activeTab !== 'HISTORY' && (
                    <div style={{marginBottom: 16, display:'flex', justifyContent:'space-between'}}>
                         <div style={{display:'flex', gap:8}}>
                             <button className="btn" style={{width:'auto', background:'transparent', border:'1px solid #555', color:'#fbbf24'}} onClick={handleFixData} disabled={refreshing}>
                                 {refreshing ? "修復中..." : "🔧 一鍵檢查與修復資料"}
                             </button>
                         </div>
                         <button className="btn" style={{width:'auto'}} onClick={handleCreate}>＋ 新增</button>
                    </div>
                )}

                {/* State Views */}
                {loading && <div className="p" style={{textAlign:'center', padding:20}}>載入中...</div>}

                {!loading && errorMsg && (
                    <div className="err" style={{textAlign:'center', padding:20}}>
                        讀取錯誤：{errorMsg}
                        <div style={{marginTop:10}}><button className="smallbtn" onClick={fetchData}>重試</button></div>
                    </div>
                )}

                {!loading && !errorMsg && cards.length === 0 && (
                    <div className="p" style={{textAlign:'center', padding: 40, border:'1px dashed #444', borderRadius:8}}>
                        {activeTab === 'INTEL' && "目前沒有法遵情資。請等待爬蟲執行或按「一鍵修復」檢查。"}
                        {activeTab === 'PROJECT' && "目前沒有專案資料。請切換到「法遵情資」建立或查看。"}
                        {activeTab === 'HISTORY' && "目前沒有歷史歸檔資料。"}
                    </div>
                )}

                {!loading && !errorMsg && (
                    <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20}}>
                        {cards.map(card => (
                            <ProjectGridItem 
                                key={card.id} 
                                card={card} 
                                onClick={() => setSelectedCard(card)} 
                                onDelete={() => handleDelete(card)}
                                canManage={isSuper}
                            />
                        ))}
                    </div>
                )}
            </div>

            {selectedCard && (
                <Modal title="詳情" onClose={() => setSelectedCard(null)} width={1000}>
                    <ComplianceCard 
                        card={selectedCard} 
                        onRefresh={() => { fetchData(); setSelectedCard(null); }} 
                        readOnly={activeTab === 'HISTORY'}
                    />
                </Modal>
            )}

            {showCrawlerConfig && <TrackedAuthoritiesDialog onClose={() => setShowCrawlerConfig(false)} onUpdate={()=>{}} />}
        </div>
    );
}
